<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import BaseMenu, { type MenuItem } from '@/components/base/BaseMenu.vue'
import { useAuthStore } from '@/stores/auth/auth.store.js'

/**
 * The operator, top-right: initials in a circle, and a menu with the two things that are
 * about THEM rather than about the panel — their account, and leaving.
 */
const auth = useAuthStore()
const router = useRouter()
const signingOut = ref(false)

const initials = computed(() => {
  const name = auth.operator?.name.trim() ?? ''
  const parts = name.split(/\s+/).filter((p) => p !== '')

  return (parts.length >= 2 ? `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}` : name.slice(0, 2)).toUpperCase()
})

const ITEMS: readonly MenuItem[] = [
  { id: 'account', label: 'My account', icon: 'user-circle' },
  { id: 'sign-out', label: 'Sign out', icon: 'sign-out', tone: 'danger' },
]

async function onSelect(id: string): Promise<void> {
  if (id === 'account') {
    await router.push({ name: 'account' })

    return
  }

  signingOut.value = true

  try {
    await auth.logout()
  } finally {
    signingOut.value = false
  }
}
</script>

<template>
  <BaseMenu
    :items="ITEMS"
    :label="`Account menu for ${auth.operator?.name ?? 'operator'}`"
    @select="onSelect"
  >
    <span
      :class="[
        'grid size-8 place-items-center rounded-full bg-action-tint text-caption font-semibold text-action',
        'ring-1 ring-action-tint-border transition-opacity duration-(--motion-fast) hover:opacity-80',
        signingOut ? 'opacity-50' : '',
      ]"
      aria-hidden="true"
      >{{ initials }}</span
    >
    <template #header>
      <p class="truncate-cell text-body font-medium text-ink">{{ auth.operator?.name }}</p>
      <p class="truncate-cell text-mono text-ink-secondary">{{ auth.operator?.email }}</p>
    </template>
  </BaseMenu>
</template>
